import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { DialogsModule } from '@progress/kendo-angular-dialog';

/** One missing field, as the server reports it. */
export interface MissingField {
  key?: string;
  label: string;
  step?: number;
}

/**
 * The structured half of a validation failure.
 *
 * The server sends this alongside the plain-text `message`, so a client that understands it can
 * render a grouped list and one that does not still has a sentence to show.
 */
export interface ValidationErrorPayload {
  /** Preferred shape: labels already bucketed by wizard step. */
  missingByStep?: Record<string | number, Array<string | MissingField>>;
  /** Flat list; grouped here by `step`, with unstepped entries collected under "Other". */
  missingFields?: MissingField[];
}

/**
 * A read-only dialog for "you cannot do this yet, and here is exactly what is missing".
 *
 * Deliberately not built on ConfirmDialogComponent: that one is a two-button destructive-action
 * confirmer, and giving it a second personality would make both harder to read. This one has a
 * single dismiss button and is sized for a list rather than a sentence.
 */
@Component({
  selector: 'app-validation-error-dialog',
  templateUrl: './validation-error-dialog.component.html',
  styleUrls: ['./validation-error-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, DialogsModule],
})
export class ValidationErrorDialogComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() title = 'Before you can continue';
  @Input() intro = '';

  /** Structured payload. Takes precedence over `message` when it has anything in it. */
  @Input() details: ValidationErrorPayload | null = null;

  /** Plain-string fallback, used when `details` is empty. */
  @Input() message = '';

  /**
   * Render `message` as HTML. Only ever set this for strings the server built from a fixed
   * label table — never for text a user typed.
   */
  @Input() allowHtml = false;

  @Input() closeLabel = 'Got it';

  @Input() stepLabels: Record<string, string> = {
    '1': 'Step 1 — Borrowers',
    '2': 'Step 2 — Financials',
    '3': 'Step 3 — Property',
  };

  @Output() closed = new EventEmitter<void>();

  /** Derived in ngOnChanges rather than a getter, so change detection is not re-running this. */
  public groups: Array<{ heading: string; items: string[] }> = [];
  public totalCount = 0;

  ngOnChanges(): void {
    this.groups = this.buildGroups();
    this.totalCount = this.groups.reduce((sum, group) => sum + group.items.length, 0);
  }

  public get hasGroups(): boolean {
    return this.groups.length > 0;
  }

  private buildGroups(): Array<{ heading: string; items: string[] }> {
    const byStep = this.details?.missingByStep;

    if (byStep) {
      return Object.keys(byStep)
        .sort()
        .map((step) => ({
          heading: this.stepLabels[step] ?? `Step ${step}`,
          items: (byStep[step] || []).map((entry) => this.labelOf(entry)).filter(Boolean),
        }))
        .filter((group) => group.items.length > 0);
    }

    const flat = this.details?.missingFields;
    if (!flat?.length) return [];

    const buckets = new Map<string, string[]>();

    for (const field of flat) {
      const key = field?.step != null ? String(field.step) : 'other';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(this.labelOf(field));
    }

    return [...buckets.keys()]
      .sort((a, b) => (a === 'other' ? 1 : b === 'other' ? -1 : a.localeCompare(b)))
      .map((key) => ({
        heading: key === 'other' ? 'Other' : (this.stepLabels[key] ?? `Step ${key}`),
        items: (buckets.get(key) || []).filter(Boolean),
      }))
      .filter((group) => group.items.length > 0);
  }

  private labelOf(entry: string | MissingField): string {
    return (typeof entry === 'string' ? entry : entry?.label ?? '').trim();
  }
}
